import React from "react";
import { Autocomplete, Box, CircularProgress, InputAdornment, TextField, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ClassFilters from "./ClassFilters";
import { ClassToolBarProps } from "../interfaces/classToolBar";
import themestyle from "../theme";

const ClassToolBar: React.FC<ClassToolBarProps> = ({
  searchQuery,
  onSearchQueryChange,
  instructorValue,
  dayValue,
  classTypeValue,
  onInstructorChange,
  onDayChange,
  onClassTypeChange,
  classOptions,
  classOptionsLoading,
  instructorOptions,
  hideBorder = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? 1 : 2,
        px: isMobile ? 0 : 2,
        py: isMobile ? 1 : 2,
        backgroundColor: "transparent",
        borderBottom: hideBorder ? "none" : "1px solid #E5E7EB",
        borderRadius: 0,
        mb: 0,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ flex: isMobile ? "1 1 auto" : "0 0 280px" }}>
        <Autocomplete
          size={isMobile ? "small" : "medium"}
          options={classOptions || []}
          value={searchQuery || null}
          onChange={(_, newValue) => onSearchQueryChange(newValue || "")}
          loading={classOptionsLoading}
          loadingText="Loading classes..."
          noOptionsText={classOptionsLoading ? "Loading..." : "No classes found"}
          disableClearable={false}
          forcePopupIcon={isMobile ? !searchQuery : true}
          sx={{
            flex: 1,
            width: isMobile ? "95%" : "100%",
            mx: isMobile ? "auto" : 0,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label=""
              placeholder="Filter by class"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {classOptionsLoading ? (
                      <CircularProgress color="inherit" size={20} sx={{ color: themestyle.colors.activeGreen }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                backgroundColor: themestyle.colors.white,
                fontSize: isMobile ? 13 : 14,
                minHeight: isMobile ? 45 : 52,
                px: isMobile ? 1 : 2,
                "& fieldset": {
                  borderWidth: "1px",
                  borderColor: themestyle.colors.bordercolor,
                },
                "&:hover fieldset": {
                  borderColor:themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
                "&.Mui-focused fieldset": {
                  borderColor: themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": {
                height: isMobile ? "25px" : "auto",
              }
            }}
            />
          )}
          slotProps={{
            listbox: {
              sx: {
                fontSize: isMobile ? 11 : 14,
                '& .MuiAutocomplete-option': {
                  padding: isMobile ? '4px 8px' : '6px 16px',
                  minHeight: isMobile ? 'auto' : undefined,
                },
              },
            },
            popper: isMobile ? {
              placement: "bottom-end",
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 4],
                  },
                },
              ],
            } : undefined,
          }}
        />
      </Box>
      {onInstructorChange && onDayChange && onClassTypeChange && (
        <Box sx={{ flex: 1, display: "flex", gap: isMobile ? 0.25 : 2, width: "100%", minWidth: 0 }}>
          <ClassFilters
            instructorValue={instructorValue || null}
            dayValue={dayValue || null}
            classTypeValue={classTypeValue || null}
            onInstructorChange={onInstructorChange}
            onDayChange={onDayChange}
            onClassTypeChange={onClassTypeChange}
            instructorOptions={instructorOptions}
          />
        </Box>
      )}
    </Box>
  );
};
export default ClassToolBar;
